import React, { useEffect, useState } from 'react';
import {
	HealthPlanUser,
	HealthPlanWorkplace,
	useHealthPlanDetailsQuery,
	useLazyHealthPlanUserQuery,
	useLazyHealthPlanWorkplaceQuery,
} from '../graphql/graphql';
import { HealthPlanTimelineItem } from '../Models/HealthPlan/HealthPlanTimelineItem';
import { usePatientTreatmentContext } from '../Modules/Patient/Components/PatientTreatment/ContextProvider';
import { getHealthPlanTimeline } from '../Modules/HealthPlan/Services/healthPlan.service';
import { useEffectAsync } from '../helpers';

export interface HealthPlan {
	id?: string | number;
	name?: string | null | undefined;
	code?: string | null | undefined;
	doctorInitialQuestionnaireCode?: string | null;
	settings?: Record<string, unknown>;
}

export interface HealthPlanContextProps {
	healthPlan: HealthPlan;
	setHealthPlan: (newState: HealthPlan) => void;
	healthPlanUser: HealthPlanUser;
	setHealthPlanUser: (newState: HealthPlanUser) => void;
	healthPlanData: any;
	setHealthPlanData: (newState: any) => void;
	activeHealthPlanUserItem: HealthPlanTimelineItem | null;
	setActiveHealthPlanUserItem: (newState: HealthPlanTimelineItem) => void;
	refetchHealthPlanUser: () => void;
	healthPlanWorkplace: HealthPlanWorkplace;
}

export interface HealthPlanContextProviderProps {
	values: HealthPlanContextProps;
	children?: React.ReactNode | React.ReactNode[];
}

export const HealthPlanContext = React.createContext({});
export const useHealthPlanContext: () => HealthPlanContextProps = () =>
	React.useContext<HealthPlanContextProps>(HealthPlanContext);

export function HealthPlanContextProvider({
	values,
	children,
}: HealthPlanContextProviderProps): JSX.Element {
	const { patientId } = usePatientTreatmentContext();
	const [healthPlan, setHealthPlan] = useState(values.healthPlan);
	const [healthPlanUser, setHealthPlanUser] = useState(values.healthPlanUser);
	const [healthPlanData, setHealthPlanData] = useState(values.healthPlanData);
	const [activeHealthPlanUserItem, setActiveHealthPlanUserItem] = useState(
		values.activeHealthPlanUserItem,
	);
	const [getHealthPlanUser] = useLazyHealthPlanUserQuery();
	const [healthPlanWorkplace, setHealthPlanWorkplace] =
		useState<HealthPlanWorkplace>();
	const [getHealthPlanWorkplace] = useLazyHealthPlanWorkplaceQuery();

	const { data } = useHealthPlanDetailsQuery({
		code: values.healthPlan.code,
	});

	const loadData = async (): Promise<void> => {
		const variables = {
			userId: patientId,
			healthPlanCode: healthPlan?.code,
		};

		//LOADING HEALTH PLAN USER AND HEALTH PLAN DATA
		const data = await getHealthPlanUser(variables);
		const newHealthPlanUser = data?.data?.healthPlanUser;
		if (newHealthPlanUser?.healthPlanId && newHealthPlanUser?.id) {
			setHealthPlanUser(newHealthPlanUser as HealthPlanUser);
			const { data: healthPlan } = await getHealthPlanTimeline({
				healthPlanId: newHealthPlanUser.healthPlanId,
				healthPlanUserId: newHealthPlanUser.id,
			});
			setHealthPlanData(Object.values(healthPlan));
		}
	};

	useEffectAsync(async () => {
		await loadData();
	}, []);

	useEffect(() => {
		if (data?.healthPlan?.id) {
			setHealthPlan((prevHealthPlan: HealthPlan) => ({
				...prevHealthPlan,
				id: String(data?.healthPlan?.id),
				doctorInitialQuestionnaireCode:
					data?.healthPlan?.doctorInitialQuestionnaireCode,
				settings: data?.healthPlan?.settings,
			}));
		}
	}, [data?.healthPlan?.id, data?.healthPlan?.doctorInitialQuestionnaireCode]);

	useEffectAsync(async () => {
		if (healthPlan.id) {
			const { data } = await getHealthPlanWorkplace({
				where: { healthPlanId: healthPlan.id.toString() },
			});

			setHealthPlanWorkplace(data?.healthPlanWorkplaceByHealthPlanId);
		}
	}, [healthPlan]);

	return (
		<HealthPlanContext.Provider
			value={{
				healthPlan,
				setHealthPlan,
				healthPlanUser,
				setHealthPlanUser,
				healthPlanData,
				setHealthPlanData,
				activeHealthPlanUserItem,
				setActiveHealthPlanUserItem,
				refetchHealthPlanUser: loadData,
				healthPlanWorkplace,
			}}>
			{children}
		</HealthPlanContext.Provider>
	);
}
